import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Image, Platform, TextInput, View } from 'react-native';
import {
  Colors,
  ColorValues,
  convertHexToBackgroundColorKeys,
  convertHexToBorderColorKeys,
  convertHexToColorKeys,
  Divider,
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import { bridge, isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { parseHTMLToTextNodes } from 'shared/src/utils/parseHTMLToReactNative/parseHTMLToTextNodes';
import { HTMLText } from 'shared/src/component/HTMLText';
import { useExperimentGroupValue } from 'shared/src/context/UserContext/useExperimentGroup';
import { useInstanceId, useNativeEvent } from 'shared/src/hooks';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { ShareRewardButton } from './components/ShareRewardButton';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import { TodayDeliveryChip } from './components/TodayDeliveryChip';
import { isDeliveryTypeIsToday } from '../../utils/isDeliveryTypeIsToday';
import { isSalesMethodIsPreOrder } from '../../utils/isSalesMethodIsPreOrder';
import { parseGoodsSnoFromDeeplink } from '../../utils/parseGoodsSnoFromDeeplink';
import {
  PromotionCountdown,
  type PromotionCountdownProps,
} from './components/PromotionCountdown/PromotionCountdown';
import { RollingNumberText } from './components/PromotionCountdown/RollingNumberText';
import { usePriceRollingTrigger } from './components/PromotionCountdown/hooks/usePriceRollingTrigger';
import {
  DIGIT_HEIGHT,
  promotionRollingTextStyle,
} from './components/PromotionCountdown/textStyle';
import { isValidPromotionLogo } from './utils/isValidPromotionLogo';

type GoodsInfoProps = {
  isSkeleton?: boolean;
};

export const GoodsInfo: React.FC<GoodsInfoProps> = React.memo(() => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();

  const { data: goodsBasicSkeleton } = usePreRenderParams();

  const goodsInfo = useMemo(() => {
    if (isNotNil(goodsBasic)) {
      return {
        name: goodsBasic.name,
        price_description: goodsBasic.price_description,
        discount_rate: goodsBasic.price_info.discount_rate,
        thumbnail_price: goodsBasic.price_info.thumbnail_price,
        consumer_price: goodsBasic.price_info.consumer,
        base_thumbnail_price:
          goodsBasic.price_info.base_thumbnail_price ?? null,
      };
    }
    return {
      name: goodsBasicSkeleton?.name ?? '',
      price_description: null,
      discount_rate: goodsBasicSkeleton?.price_info.discount_rate ?? 0,
      thumbnail_price: goodsBasicSkeleton?.price_info.thumbnail_price ?? 0,
      consumer_price: goodsBasicSkeleton?.price_info.consumer ?? 0,
      base_thumbnail_price: null as number | null,
    };
  }, [goodsBasic, goodsBasicSkeleton]);

  const showNudging = isNotNil(goodsInfo.price_description?.nudge_text);

  // nudge_text HTML을 색상/굵기 정보를 가진 텍스트 노드로 분해
  const nudgeText = goodsInfo.price_description?.nudge_text;
  const nudgeNodes = useMemo(() => {
    if (!showNudging || !nudgeText) return null;
    const nodes = parseHTMLToTextNodes(nudgeText);
    return nodes.length > 0 ? nodes : null;
  }, [showNudging, nudgeText]);

  const nudgeTextColor =
    goodsInfo.price_description?.nudge_text_color ||
    Colors.content_pink_primary;
  const nudgeBgColor =
    goodsInfo.price_description?.nudge_background_color || '#FFDFE4';
  const nudgeAreaBgColor =
    goodsInfo.price_description?.nudge_area_background_color || '#FFF4F6';

  // B=타이머 스트립 / C·D=로고 헤더
  const experimentGroup = useExperimentGroupValue({ sno: 'EXP1841' });
  const promotionCountdown =
    goodsInfo.price_description?.promotion_countdown ?? null;
  const nudgeIcon = goodsInfo.price_description?.nudge_icon ?? null;

  const countdownConfig = useMemo<PromotionCountdownProps | null>(() => {
    // promotion_countdown이 없으면 A(현행 넛지). 만료돼도 스트립 유지(00:00:00 고정)
    if (isNil(promotionCountdown)) return null;
    const {
      text_color,
      background_color,
      finished_at,
      logo_image,
      deeplink,
      logging,
    } = promotionCountdown;
    // 폴백 없이: 렌더 필수값(색상·만료시각)이 없으면 미노출
    if (isNil(text_color) || isNil(background_color) || !finished_at)
      return null;

    switch (experimentGroup) {
      case 'B':
        return {
          variant: 'timer',
          finishedAt: finished_at,
          textColor: text_color,
          backgroundColor: background_color,
          logoImage: null,
          deeplink: null,
          logging: null,
        };
      case 'C':
        // 유효한 로고가 없으면 미노출 (timer 폴백 안 함). C는 탭 불가.
        if (!isValidPromotionLogo(logo_image)) return null;
        return {
          variant: 'banner',
          finishedAt: finished_at,
          textColor: text_color,
          backgroundColor: background_color,
          logoImage: logo_image,
          deeplink: null,
          logging: null,
        };
      case 'D':
        if (!isValidPromotionLogo(logo_image)) return null;
        // 빈 deeplink는 C와 동일하게 탭/chevron 없음
        return {
          variant: 'banner',
          finishedAt: finished_at,
          textColor: text_color,
          backgroundColor: background_color,
          logoImage: logo_image,
          deeplink:
            isNotNil(deeplink) && deeplink.length > 0 ? deeplink : null,
          logging,
        };
      default:
        return null;
    }
  }, [promotionCountdown, experimentGroup]);

  const logAnalyticsEvent = useLogAnalyticsEvent();
  const instanceId = useInstanceId();

  // EXP1841 노출 트래킹: 넛지 조건문구(전 버킷 공통 신호)가 있으면 그룹 무관 진입당 1회.
  // warm 서피스 재진입은 mount가 유지되므로 incomingDeeplink로 latch를 푼다.
  // goodsSno는 route 기준(useGoodsSno) — basic 미도착 시에도 롤링 트리거와 동일하게 latch 리셋.
  const goodsSno = useGoodsSno();
  const isExperimentTarget = isNotNil(
    goodsInfo.price_description?.nudge_condition_text,
  );
  const hasSentExpTarget = useRef(false);
  const tryLogExpTarget = useCallback(() => {
    if (hasSentExpTarget.current) return;
    if (!isExperimentTarget) return;
    hasSentExpTarget.current = true;
    logAnalyticsEvent({
      name: 'CHECK_EXP_1841_TARGET',
      analytics: { GOODS_SNO: goodsSno.toString() },
    });
  }, [isExperimentTarget, goodsSno, logAnalyticsEvent]);

  useEffect(() => {
    tryLogExpTarget();
  }, [tryLogExpTarget]);

  useNativeEvent('incomingDeeplink', instanceId, ({ incomingDeeplink }) => {
    if (parseGoodsSnoFromDeeplink(incomingDeeplink) !== goodsSno) return;
    hasSentExpTarget.current = false;
    tryLogExpTarget();
  });

  // 가격 롤링: 칩이 화면에 100% 노출 + 스크롤 idle일 때만 재생 (노출 전 조기 재생 방지)
  const priceChipRef = useRef<View>(null);
  const { shouldPlay: shouldPlayPriceRolling, onLayout: onPriceChipLayout } =
    usePriceRollingTrigger(priceChipRef, isNotNil(countdownConfig));

  if (isNil(goodsBasic)) {
    return (
      <Flex>
        <Divider size={'small'} />
        <Flex padding={{ horizontal: 16, vertical: 16, top: 28 }} gap={12}>
          <Typography type={'body1'}>{goodsInfo.name}</Typography>
          <Flex gap={2}>
            <Flex.Row alignItems={'center'} gap={4}>
              {goodsInfo.price_description && (
                <Flex.Row alignItems={'center'} gap={2}>
                  {isNotNil(goodsInfo.price_description.icon_url) && (
                    <RemoteImage
                      uri={goodsInfo.price_description.icon_url}
                      width={16}
                      height={16}
                    />
                  )}
                  <Typography
                    type={'subtitle4'}
                    color={'content_secondary'}
                    numberOfLines={1}
                  >
                    {goodsInfo.price_description.text}
                  </Typography>
                </Flex.Row>
              )}
              {goodsInfo.discount_rate > 0 && (
                <Typography
                  type={'body2'}
                  color={'content_disabled'}
                  textDecorationLine={'line-through'}
                >
                  {localizeNumber(goodsInfo.consumer_price ?? 0)}원
                </Typography>
              )}
            </Flex.Row>
            <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
              <Flex.Row gap={4}>
                {goodsInfo.discount_rate > 0 && (
                  <Typography type={'h4'} color={'content_pink_primary'}>
                    {goodsInfo.discount_rate}%
                  </Typography>
                )}
                <Typography type={'h4'}>
                  {localizeNumber(goodsInfo.thumbnail_price)}원
                </Typography>
              </Flex.Row>
              <ShareRewardButton showNudging={showNudging} />
            </Flex.Row>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex>
      <Divider size={'small'} />
      <Flex padding={{ horizontal: 16, vertical: 16 }} gap={12}>
        <Flex.Row gap={4} alignItems={'center'}>
          {!isSalesMethodIsPreOrder(goodsBasic) &&
            isDeliveryTypeIsToday(goodsBasic) && (
              <TodayDeliveryChip
                hourStr={
                  goodsBasic?.market.today_delivery_order_deadline_time ?? null
                }
              />
            )}
          {goodsDetail?.chips.map(chip => {
            if (chip.type === 'DITTO_IMAGE') {
              return (
                <Flex
                  key={chip.name}
                  onPress={
                    isNotNil(chip.display_info)
                      ? () => bridge.toast(chip.display_info!)
                      : undefined
                  }
                >
                  <RemoteImage
                    uri={chip.meta.url}
                    width={20 * chip.meta.ratio}
                    height={20}
                  />
                </Flex>
              );
            }
            if (chip.type === 'DITTO_TEXT') {
              return (
                <Flex.Center
                  key={chip.name}
                  borderWidth={chip.meta.border_width}
                  borderRadius={chip.meta.rounded_corner_radius}
                  borderColor={
                    convertHexToBorderColorKeys(chip.meta.border_color) ??
                    undefined
                  }
                  height={20}
                  borderColorValue={
                    convertHexToBorderColorKeys(chip.meta.border_color) === null
                      ? chip.meta.border_color
                      : undefined
                  }
                  backgroundColor={
                    convertHexToBackgroundColorKeys(chip.meta.bg_color) ??
                    undefined
                  }
                  backgroundColorValue={
                    convertHexToBackgroundColorKeys(chip.meta.bg_color) === null
                      ? chip.meta.bg_color
                      : undefined
                  }
                  padding={{
                    horizontal: 4,
                  }}
                >
                  <Typography
                    color={
                      convertHexToColorKeys(chip.meta.text_color) ?? undefined
                    }
                    rawColor={
                      convertHexToColorKeys(chip.meta.text_color)
                        ? undefined
                        : chip.meta.text_color
                    }
                    type={chip.meta.text_style}
                  >
                    {chip.name}
                  </Typography>
                </Flex.Center>
              );
            }
          })}
        </Flex.Row>
        <TextInput
          value={goodsInfo.name}
          editable={false}
          multiline
          allowFontScaling={false}
          scrollEnabled={false}
          style={{
            fontSize: 16,
            lineHeight: 20,
            fontFamily: Platform.select({
              ios: 'Pretendard-Regular',
              android: 'pretendard_regular',
            }),
            letterSpacing: -0.4,
            color: ColorValues.gray70,
            padding: 0,
          }}
        />
        {isNotNil(goodsDetail) &&
          isNotNil(goodsDetail.product_info_provision_notice) &&
          isNotNil(goodsDetail.product_info_provision_notice.country) &&
          goodsDetail.product_info_provision_notice.country !== '' && (
            <Flex margin={{ top: -8 }}>
              <Typography type={'body3'} rawColor={ColorValues.gray70}>
                원산지 : {goodsDetail.product_info_provision_notice.country}
              </Typography>
            </Flex>
          )}
        {showNudging ? (
          <Flex borderRadius={8} overflow={'hidden'}>
            {isNotNil(countdownConfig) && (
              <PromotionCountdown {...countdownConfig} />
            )}
            <Flex
              backgroundColor={'pink3'}
              backgroundColorValue={nudgeAreaBgColor}
              padding={{ horizontal: 12, vertical: 12 }}
            >
              {isNotNil(goodsInfo.price_description?.text) &&
                goodsInfo.price_description.text.length > 0 && (
                  <Flex.Row alignItems={'center'} gap={4}>
                    <Flex.Row gap={2}>
                      {isNotNil(goodsInfo.price_description.icon_url) && (
                        // eslint-disable-next-line no-restricted-syntax -- tintColor 필요, RemoteImage 미지원
                        <Image
                          source={{ uri: goodsInfo.price_description.icon_url }}
                          style={{
                            width: 16,
                            height: 16,
                            tintColor: nudgeTextColor,
                          }}
                        />
                      )}
                      <Typography
                        type={'subtitle4'}
                        rawColor={'rgba(0,0,0,0.5)'}
                        numberOfLines={1}
                      >
                        {goodsInfo.price_description.text}
                      </Typography>
                    </Flex.Row>
                    {goodsInfo.discount_rate > 0 && (
                      <Flex margin={{ left: 2 }}>
                        <Typography
                          type={'body2'}
                          rawColor={'rgba(0,0,0,0.2)'}
                          textDecorationLine={'line-through'}
                        >
                          {localizeNumber(goodsInfo.consumer_price ?? 0)}원
                        </Typography>
                      </Flex>
                    )}
                  </Flex.Row>
                )}
              {isNotNil(goodsInfo.base_thumbnail_price) && (
                <Flex
                  margin={{
                    top: isNotNil(goodsInfo.price_description?.text) ? 2 : 0,
                  }}
                >
                  <Typography type={'h5'} rawColor={'rgba(0,0,0,0.5)'}>
                    {localizeNumber(goodsInfo.base_thumbnail_price)}원
                  </Typography>
                </Flex>
              )}
              <Flex.Row
                alignItems={'center'}
                justifyContent={'space-between'}
                margin={{ top: 4 }}
              >
                <Flex.Row gap={4}>
                  {goodsInfo.discount_rate > 0 && (
                    <Typography
                      type={'h4'}
                      color={'content_pink_primary'}
                      rawColor={nudgeTextColor}
                    >
                      {goodsInfo.discount_rate}%
                    </Typography>
                  )}
                  <Typography type={'h4'}>
                    {localizeNumber(goodsInfo.thumbnail_price)}원
                  </Typography>
                </Flex.Row>
                <ShareRewardButton
                  showNudging={showNudging}
                  nudgeTextColor={nudgeTextColor}
                />
              </Flex.Row>
              {isNotNil(nudgeNodes) && (
                <>
                  <Flex
                    height={1}
                    backgroundColorValue={Colors.border_transparent}
                    margin={{ top: 12 }}
                  />
                  <Flex.Row alignItems={'center'} gap={4} margin={{ top: 12 }}>
                    {isNotNil(
                      goodsInfo.price_description?.nudge_condition_text,
                    ) && (
                      <Typography
                        type={'subtitle3'}
                        rawColor={'rgba(0,0,0,0.5)'}
                      >
                        {goodsInfo.price_description!.nudge_condition_text}
                      </Typography>
                    )}
                    <Flex.Row
                      ref={priceChipRef}
                      onLayout={onPriceChipLayout}
                      alignItems={'center'}
                      gap={2}
                      backgroundColor={'pink10'}
                      backgroundColorValue={nudgeBgColor}
                      borderRadius={4}
                      padding={{ horizontal: 4, vertical: 2 }}
                    >
                      {/* 실험군(B/C/D): RollingNumberText를 유지하고 play로만 롤업.
                          A/폴백: HTMLText 정적. play 전에도 목표 금액이 보여 교체 마운트 깜빡임 없음. */}
                      {isNotNil(countdownConfig) ? (
                        <Flex.Row alignItems={'center'}>
                          {nudgeNodes.map((node, index) => (
                            <RollingNumberText
                              key={index}
                              text={node.text}
                              digitHeight={DIGIT_HEIGHT}
                              // 노드 무관 SemiBold 고정(subtitle3=SemiBold, 정적 HTMLText 경로와 일치)
                              textStyle={promotionRollingTextStyle(
                                node.fontSize ?? 13,
                                node.color ?? nudgeTextColor,
                              )}
                              mode={'price'}
                              play={shouldPlayPriceRolling}
                            />
                          ))}
                        </Flex.Row>
                      ) : isNotNil(nudgeText) ? (
                        <HTMLText type={'subtitle3'} rawColor={nudgeTextColor}>
                          {nudgeText}
                        </HTMLText>
                      ) : null}
                      {isNotNil(nudgeIcon) && (
                        <RemoteImage
                          uri={nudgeIcon.url}
                          width={nudgeIcon.width}
                          height={nudgeIcon.height}
                        />
                      )}
                    </Flex.Row>
                  </Flex.Row>
                </>
              )}
            </Flex>
          </Flex>
        ) : (
          <Flex gap={2}>
            <Flex.Row alignItems={'center'} gap={4}>
              {goodsInfo.price_description && (
                <Flex.Row alignItems={'center'} gap={2}>
                  {isNotNil(goodsInfo.price_description.icon_url) && (
                    <RemoteImage
                      uri={goodsInfo.price_description.icon_url}
                      width={16}
                      height={16}
                    />
                  )}
                  <Typography
                    type={'subtitle4'}
                    color={'content_secondary'}
                    numberOfLines={1}
                  >
                    {goodsInfo.price_description.text}
                  </Typography>
                </Flex.Row>
              )}
              {goodsInfo.discount_rate > 0 && (
                <Typography
                  type={'body2'}
                  color={'content_disabled'}
                  textDecorationLine={'line-through'}
                >
                  {localizeNumber(goodsInfo.consumer_price ?? 0)}원
                </Typography>
              )}
            </Flex.Row>
            <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
              <Flex.Row gap={4}>
                {goodsInfo.discount_rate > 0 && (
                  <Typography type={'h4'} color={'content_pink_primary'}>
                    {goodsInfo.discount_rate}%
                  </Typography>
                )}
                <Typography type={'h4'}>
                  {localizeNumber(goodsInfo.thumbnail_price)}원
                </Typography>
              </Flex.Row>
              <ShareRewardButton showNudging={showNudging} />
            </Flex.Row>
          </Flex>
        )}
      </Flex>
      <Divider size={'small'} />
    </Flex>
  );
});
